import {
  AdditionalConfiguration,
  NumericFormatType,
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationControl,
  ReportSectionConfigurationControlType,
  ReportSectionDataSection,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';
import { CurrentPeerOverweightInstitutionsModel } from './current-peer-overweight-institutions.model';

export class CurrentPeerOverweightInstitutionsConfiguration extends ReportSectionConfigurationBase {
  typeName = 'CurrentPeerOverweightInstitutionsConfiguration';
  nodataLocalizationKeyName = 'currentPeerOverweightInstitutions';
  readonly reportSection = ReportSection.CurrentPeerOverweightInstitutions;
  readonly name = 'currentPeerOverweightInstitutions';
  readonly displayName = 'Current Peer Overweight Institutions - Global';
  readonly applicableReportTypes = [ReportType.PeerOwnershipReport];
  readonly reportViewModelSections = [ReportViewModelSection.PeerOwnershipReport_CurrentPeerOverweightInstitutions];
  readonly minSize = 2;
  readonly maxSize = 4;
  readonly defaultSize = 4;
  override maxGridUnitsAllowed = 14;
  readonly availableDataSections: ReportSectionDataSection[] = [
    {
      name: 'currentPeerOverweightInstInstitution',
      displayName: 'Institution',
      isSelected: true,
      canConfigure: false,
      unit: 2.7,
      property: 'institutionName',
      isStatic: true,
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'currentPeerOverweightInstShares',
      displayName: 'Shares',
      isSelected: true,
      canConfigure: true,
      unit: 0.7,
      property: 'shares',
      type: ReportSectionConfigurationControlType.BigNumber
    },
    {
      name: 'currentPeerOverweightInstShareChange',
      displayName: 'Share Change',
      isSelected: true,
      canConfigure: true,
      unit: 0.9,
      property: 'shareChange',
      type: ReportSectionConfigurationControlType.BigNumber
    },
    {
      name: 'currentPeerOverweightInstEquityAssets',
      displayName: 'Equity Assets',
      isSelected: true,
      canConfigure: true,
      unit: 0.6,
      property: 'equityAssetsMm',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'currentPeerOverweightInstDrEquityAssets',
      displayName: 'DR Equity Assets',
      isSelected: true,
      canConfigure: true,
      unit: 0.6,
      property: 'drEquityAssetsMm',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'currentPeerOverweightInstValue',
      displayName: 'Value',
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      property: 'value',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'currentPeerOverweightInstSectorValue',
      displayName: 'Sector Value',
      isSelected: true,
      canConfigure: true,
      unit: 0.6,
      property: 'sectorValue',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'currentPeerOverweightInstSectorValueChange',
      displayName: 'Sector Value Change',
      isSelected: true,
      canConfigure: true,
      unit: 0.7,
      property: 'sectorValueChange',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'currentPeerOverweightInstSectorPctPortfolio',
      displayName: 'Sector % Portfolio',
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      property: 'sectorPctPortfolio',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.Percentage
    },
    {
      name: 'currentPeerOverweightInstRegionValue',
      displayName: 'Region Value',
      isSelected: true,
      canConfigure: true,
      unit: 0.6,
      property: 'regionValue',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'currentPeerOverweightInstRegionValueChange',
      displayName: 'Region Value Change',
      isSelected: true,
      canConfigure: true,
      unit: 0.7,
      property: 'regionValueChange',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'currentPeerOverweightInstRegionPctPortfolio',
      displayName: 'Region % Portfolio',
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      property: 'regionPctPortfolio',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.Percentage
    },
    {
      name: 'currentPeerOverweightInstPeerValue',
      displayName: 'Peer Value',
      isSelected: true,
      canConfigure: true,
      unit: 0.6,
      property: 'peerValue',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'currentPeerOverweightInstPeerValueChange',
      displayName: 'Peer Value Change',
      isSelected: true,
      canConfigure: true,
      unit: 0.7,
      property: 'peerValueChange',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'currentPeerOverweightInstPeersNumHeld',
      displayName: 'Peers # Held',
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      property: 'peersNumHeld',
      type: ReportSectionConfigurationControlType.Number
    },
    {
      name: 'currentPeerOverweightInstPurchasingPower',
      displayName: 'Purchasing Power',
      isSelected: true,
      canConfigure: true,
      unit: 0.7,
      property: 'purchasingPower',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'currentPeerOverweightInstSuitabilityScore',
      displayName: 'Suitability Score',
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      property: 'suitabilityScore',
      type: ReportSectionConfigurationControlType.Decimal
    },
    {
      name: 'currentPeerOverweightInstStyle',
      displayName: 'Style',
      isSelected: true,
      canConfigure: true,
      unit: 0.6,
      property: 'style',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'currentPeerOverweightInstCity',
      displayName: 'City',
      isSelected: true,
      canConfigure: true,
      unit: 0.8,
      property: 'city',
      type: ReportSectionConfigurationControlType.Text
    }
  ];

  override additionalConfiguration: AdditionalConfiguration = {
    isTotalRowShown: true
  };

  override configurationControls: ReportSectionConfigurationControl[] = [
    {
      name: 'currentPeerOverweightInstShowTotals',
      displayName: 'Show Total',
      type: 'totalRowControl'
    }
  ];
}

export interface CurrentPeerOverweightInstitutionsDataBag {
  [ReportViewModelSection.PeerOwnershipReport_CurrentPeerOverweightInstitutions]: CurrentPeerOverweightInstitutionsModel;
}
